import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { fetchUsers, createUser, updateUser } from '../services/userService';
import { updateSubscriptionFields } from '../services/subscriptionService';
import { textFieldStyles, formControlStyles } from '../styles';
import { v4 as uuidv4 } from 'uuid';
import { skillsCategories } from '../config/skillsConfig';
import { getSkillLabel } from '../utils/skillsUtils';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { useOrganization } from '../contexts/OrganizationContext';
import { getSubscriptionByGuid } from '../services/subscriptionService';
import { CAPABILITIES, hasCapability } from '../services/capabilitiesService';
import CONFIG from '../config/config';

import {
    Box,
    Button,
    Checkbox,
    TextField,
    Typography,
    MenuItem,
    Select,
    InputLabel,
    FormControl,
    Chip,
    LinearProgress,
    OutlinedInput,
    FormControlLabel
} from '@mui/material';

const PersonnelDetails = () => {
    const categoryColor = '#d9f9d9';
    const navigate = useNavigate();
    const { userGuid } = useParams();
    const [userData, setUserData] = useState({
        userGuid: null,
        firstName: null,
        lastName: null,
        email: null,
        phoneNumber: null,
        optOutLocSharing: false,
        isNewsletterSubscribed: true,
        subsPlan: 0,
        certificateType: 1, // Default to Part 107
        certificateNumberOrToken: null,
        issuedRenewedDate: null,
        version: 1,
        createdAt: null,
        updatedAt: null,
    });
    const { selectedOrg } = useOrganization();
    const [subscription, setSubscription] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [isEdit, setIsEdit] = useState(!!userGuid);
    const [userSkills, setUserSkills] = useState({});
    const [isDirty, setIsDirty] = useState(false);
    const [modifiedFields, setModifiedFields] = useState({});
    const [validationErrors, setValidationErrors] = useState({});
    const [isPilot, setIsPilot] = useState(false);

    useEffect(() => {
        const loadSubscriptionData = async () => {
            if (selectedOrg) {
                try {
                    const subscriptionData = await getSubscriptionByGuid(selectedOrg);
                    if (subscriptionData) {
                        setSubscription(subscriptionData.subscription);
                    } else {
                        throw new Error('Subscription not found');
                    }
                } catch (error) {
                    console.error('Error loading subscription data:', error);
                    setError('Failed to load subscription data.');
                }
            } else {
                setSubscription(null); // Clear subscription if no subscriptionGuid exists
            }
        };

        loadSubscriptionData();
    }, [selectedOrg]);

    useEffect(() => {
        const loadUserDetails = async () => {
            if (isEdit) {
                try {
                    const data = await fetchUsers(selectedOrg);
                    //console.log('Fetched Users:', data);
                    const user = data.find((u) => u.userGuid === userGuid);
                    //console.log('Matched User:', user); // Log the matched user
                    if (user) {
                        // Remove serverUpdatedAt property
                        const { serverUpdatedAt, ...filteredUser } = user;

                        setUserData({ ...filteredUser });

                        // Populate userSkills state based on the fetched user data
                        const initialSkills = skillsCategories.reduce((acc, category) => {
                            acc[category.key] = user.skills?.[category.key] || []; // Default to an empty array if no skills are found
                            return acc;
                        }, {});
                        setUserSkills(initialSkills);
                        setIsPilot(user.skills?.operational?.includes('remote_pic') || false);
                    } else {
                        setError('User not found.');
                    }
                } catch (err) {
                    setError('Failed to load user details.');
                    console.error('Error loading user details:', err);
                } finally {
                    setLoading(false);
                }
            } else {
                setLoading(false); // No need to load data for a new user
            }
        };

        loadUserDetails();
    }, [isEdit, userGuid, selectedOrg]);

    // Initialize skills
    useEffect(() => {
        if (!isEdit) {
            const initialSkills = skillsCategories.reduce((acc, category) => {
                acc[category.key] = [];
                return acc;
            }, {});
            setUserSkills(initialSkills);
        }
    }, [isEdit]);

    /*
        useEffect(() => {
            // Initialize userSkills state for each category
            const initialSkills = skillsCategories.reduce((acc, category) => {
                acc[category.key] = [];
                return acc;
            }, {});
            setUserSkills(initialSkills);
        }, []);
    */
    const validateFields = () => {
        const errors = {};
        if (!userData.firstName?.trim()) errors.firstName = 'First Name is required.';
        if (!userData.lastName?.trim()) errors.lastName = 'Last Name is required.';
        if (!userData.email?.trim()) {
            errors.email = 'Email is required.';
        } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(userData.email)) {
            errors.email = 'Invalid email format.';
        }
        setValidationErrors(errors);
        return Object.keys(errors).length === 0;
    };

    const handleSkillChange = (categoryKey, selectedKeys) => {
        setUserSkills((prevSkills) => ({
            ...prevSkills,
            [categoryKey]: selectedKeys,
        }));

        setModifiedFields((prevFields) => ({
            ...prevFields,
            skills: {
                ...prevFields.skills,
                [categoryKey]: selectedKeys,
            },
        }));

        setIsDirty(true);
    };

    const handleChange = (e) => {
        const { name, value } = e.target;

        setIsDirty(true);
        setUserData((prevData) => ({
            ...prevData,
            [name]: value,
        }));

        setModifiedFields((prevFields) => ({
            ...prevFields,
            [name]: value,
        }));
    };

    const handleSave = async () => {
        if (!validateFields()) {
            return; // Prevent save if validation fails
        }

        try {
            if (isPilot) {
                // Fetch all users for the selected organization
                const users = await fetchUsers(selectedOrg);

                // Check if another user is already a pilot (has remote_pic skill)
                const otherPilotExists = users.some(
                    (user) =>
                        user.skills?.operational?.includes('remote_pic') &&
                        user.userGuid !== userGuid // Exclude the current user being edited
                );

                if (otherPilotExists) {
                    alert(
                        'This subscription is limited to one pilot. Please remove the "Is Pilot" designation from the other user before assigning it to this user.'
                    );
                    return; // Prevent save if another pilot exists
                }
            }

            if (isEdit) {
                // Increment version and update modified fields
                const updatedData = {
                    ...modifiedFields,
                    version: (userData.version || 1) + 1, // Increment version
                    skills: userSkills,
                };
                // console.log('Updating user with:', updatedData);
                await updateUser(userGuid, updatedData);
            } else {
                // Generate a new GUID for the user
                const newGuid = uuidv4();
                const now = new Date().toISOString(); // Get the current date/time in ISO8601 format
                const newUserPayload = {
                    user: {
                        ...userData,
                        userGuid: newGuid,
                        skills: userSkills,
                        createdAt: now,
                        updatedAt: now,
                    }
                };

                //console.log('Creating new user with:', newUserPayload);
                const createdUser = await createUser(newUserPayload, selectedOrg);

                if (createdUser) {
                    //console.log('Successfully created user. Updating subscription with new user:', newGuid);
                    await updateSubscriptionFields(selectedOrg, { assignedUsers: [newGuid] }, 'add');
                } else {
                    throw new Error('Failed to create user. Subscription update aborted.');
                }
            }
            setIsDirty(false);
            setModifiedFields({});
            navigate('/personnel');
        } catch (error) {
            if (error.message.includes('email already exists')) {
                // Alert the user if the email already exists
                alert(error.message);
            } else {
                console.error('Error saving user:', error);
                alert('An error occurred while saving the user.');
            }
        }
    };

    const handleCancel = () => {
        setIsDirty(false);
        setModifiedFields({});
        navigate('/personnel');
    };

    const handleBackToPersonnel = () => {
        navigate('/personnel');
    };

    useEffect(() => {
        const isRemotePicPresent = userSkills.operational?.includes('remote_pic') || false;
        if (isRemotePicPresent !== isPilot) {
            setIsPilot(isRemotePicPresent);
        }
    }, [userSkills.operational, isPilot]);

    const handleCheckboxChange = (event) => {
        const checked = event.target.checked;
        setIsPilot(checked);

        setUserSkills((prevSkills) => {
            const updatedOperationalSkills = checked
                ? [...new Set([...prevSkills.operational, 'remote_pic'])] // Add 'remote_pic' if not present
                : prevSkills.operational.filter((skill) => skill !== 'remote_pic'); // Remove 'remote_pic'

            return {
                ...prevSkills,
                operational: updatedOperationalSkills,
            };
        });

        setModifiedFields((prevFields) => ({
            ...prevFields,
            skills: {
                ...prevFields.skills,
                operational: checked
                    ? [...new Set([...(prevFields.skills?.operational || []), 'remote_pic'])]
                    : (prevFields.skills?.operational || []).filter((skill) => skill !== 'remote_pic'),
            },
        }));

        setIsDirty(true);
    };

    if (loading) {
        return (
            <Box
                sx={{
                    width: '100%',
                    // padding: '16px',
                    backgroundColor: '#f9f9f9',
                }}
            // sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}
            >
                <Typography
                    sx={{
                        textAlign: 'center',
                        //marginBottom: 2,
                        color: '#2c3e50',
                        fontWeight: 'bold',
                    }}
                >
                    Loading Personnel...
                </Typography>
                <LinearProgress
                    sx={{
                        height: 4,
                        borderRadius: 4,
                        backgroundColor: '#e0e0e0', // Track color
                        '& .MuiLinearProgress-bar': {
                            borderRadius: 4,
                            backgroundColor: '#3f51b5', // Bar color
                        },
                    }}
                />
            </Box>
        );
    }

    if (error) {
        return (
            <Box sx={{ textAlign: 'center', padding: 4 }}>
                <Typography color="error">{error}</Typography>
            </Box>
        );
    }

    return (
        <Box sx={{ paddingLeft: 4, paddingRight: 4, paddingBottom: 4, marginBottom: '80px', }}>
            {/* Back to Personnel Button */}
            <Button
                variant="text"
                onClick={handleBackToPersonnel}
                startIcon={<ArrowBackIcon />}
                sx={{
                    marginBottom: '16px',
                    color: '#2c3e50',
                    textTransform: 'none',
                    fontWeight: 400,
                    '&:hover': {
                        backgroundColor: '#f0f0f0',
                    },
                }}
            >
                Personnel List
            </Button>
            <Typography variant="h5" gutterBottom>
                {isEdit ? 'Edit Personnel Details' : 'Add Personnel'}
            </Typography>

            {/* Is Pilot Checkbox */}
            <FormControlLabel
                control={
                    <Checkbox
                        checked={isPilot}
                        onChange={handleCheckboxChange}
                        color="primary"
                    />
                }
                label="Is Pilot"
                sx={{
                    marginTop: 2,
                    marginBottom: 2,
                }}
            />

            {/* First Name Row */}
            <TextField
                label="First Name"
                name="firstName"
                value={userData.firstName}
                onChange={handleChange}
                fullWidth
                margin="normal"
                error={!!validationErrors.firstName}
                helperText={validationErrors.firstName}
                sx={textFieldStyles}
            />

            {/* Last Name Row */}
            <TextField
                label="Last Name"
                name="lastName"
                value={userData.lastName}
                onChange={handleChange}
                fullWidth
                margin="normal"
                error={!!validationErrors.lastName}
                helperText={validationErrors.lastName}
                sx={textFieldStyles}
            />

            {/* Email Row */}
            <TextField
                label="Email"
                name="email"
                value={userData.email}
                onChange={handleChange}
                fullWidth
                margin="normal"
                error={!!validationErrors.email}
                helperText={validationErrors.email}
                sx={textFieldStyles}
            />

            {/* Phone Number Row */}
            <TextField
                label="Phone"
                name="phoneNumber"
                value={userData.phoneNumber}
                onChange={handleChange}
                fullWidth
                margin="normal"
                sx={textFieldStyles}
            />

            {/* Skills */}
            {hasCapability(subscription?.planId, CAPABILITIES.SKILLS) ? (
                <Box sx={{
                    marginTop: 2,
                    marginBottom: 3,
                    border: `2px solid ${categoryColor}`,
                    borderRadius: '8px',
                    paddingTop: 2,
                    paddingLeft: 2,
                    paddingRight: 2,
                    backgroundColor: `${categoryColor}40`,
                }}>
                    <Typography variant="h5" gutterBottom sx={{ paddingBottom: 2 }}>
                        Skills
                    </Typography>
                    {skillsCategories.map((category, index) => (
                        <Box key={index} sx={{ marginBottom: 4 }}>
                            <FormControl fullWidth>
                                <InputLabel id={`${category.key}-label`} sx={{ color: '#2c3e50' }}>
                                    {category.title}
                                </InputLabel>
                                <Select
                                    labelId={`${category.key}-label`}
                                    id={`${category.key}-select`}
                                    multiple
                                    value={userSkills[category.key] || []}
                                    onChange={(e) =>
                                        handleSkillChange(category.key, e.target.value)
                                    }
                                    input={<OutlinedInput label={category.title} />}
                                    renderValue={(selectedKeys) => (
                                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                            {selectedKeys.map((key) => {
                                                const skill = category.skills.find((s) => s.key === key);
                                                return (
                                                    <Chip
                                                        key={key}
                                                        label={skill.label}
                                                        sx={{ backgroundColor: '#e0e0e0', color: '#2c3e50' }}
                                                    />
                                                );
                                            })}
                                        </Box>
                                    )}
                                    sx={{
                                        backgroundColor: '#ffffff',
                                        '& .MuiSelect-icon': { color: '#6c757d' },
                                    }}
                                >
                                    {category.skills.map((skill) => (
                                        <MenuItem
                                            key={skill.key}
                                            value={skill.key}
                                            sx={{
                                                color: '#2c3e50',
                                                '&.Mui-selected': { backgroundColor: '#e3f2fd !important', color: '#2c3e50' },
                                            }}
                                        >
                                            {skill.label}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Box>
                    ))}
                </Box>
            ) : null}

            {/* Certificate Number or Token */}
            <TextField
                //label={selectedCertificateType === 1 ? 'Part 107 Certificate Number' : 'TRUST Authorization Token'}
                label='Part 107 Certificate Number'
                name="certificateNumberOrToken"
                value={userData.certificateNumberOrToken}
                onChange={handleChange}
                fullWidth
                margin="normal"
                sx={textFieldStyles}
            />

            <TextField
                label="Certificate Issued/Renewed Date"
                name="issuedRenewedDate"
                value={userData.issuedRenewedDate}
                onChange={handleChange}
                fullWidth
                margin="normal"
                sx={textFieldStyles}
            />

            {/* Certificate Type Row */}
            {/*
            <Box sx={{ marginBottom: 2, textAlign: 'center' }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 'bold', color: '#2c3e50', marginBottom: 1 }}>
                    Certificate
                </Typography>
                <ToggleButtonGroup
                    value={selectedCertificateType}
                    exclusive
                    onChange={handleToggleChange}
                    sx={{
                        display: 'flex',
                        justifyContent: 'center',
                        '& .MuiToggleButton-root': {
                            padding: '6px 24px',
                            textTransform: 'none',
                            border: '1px solid #dcdcdc',
                            color: '#2c3e50',
                            backgroundColor: '#e0e0e0', // Default unselected background
                            '&.Mui-selected': {
                                backgroundColor: '#ffffff', // Selected background
                                color: '#2c3e50',
                                fontWeight: 'bold',
                                borderColor: '#42a5f5', // Ensure full border when selected
                                zIndex: 1, // Bring selected button to the front
                            },
                            '&:hover': {
                                backgroundColor: '#e3f2fd',
                            },
                        },
                    }}
                >
                    <ToggleButton value={1}>
                        <Box sx={{ textAlign: 'center' }}>
                            <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                                Part 107
                            </Typography>
                            <Typography variant="caption" sx={{ color: '#6c757d' }}>
                                (Part 107 Certificate)
                            </Typography>
                        </Box>
                    </ToggleButton>
                    <ToggleButton value={0}>
                        <Box sx={{ textAlign: 'center' }}>
                            <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                                Recreational
                            </Typography>
                            <Typography variant="caption" sx={{ color: '#6c757d' }}>
                                (TRUST Certificate)
                            </Typography>
                        </Box>
                    </ToggleButton>
                </ToggleButtonGroup>
            </Box>
            */}

            {/*
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2, paddingTop: 6, paddingBottom: 4 }}>
                <Button variant="outlined" onClick={() => navigate('/personnel')}>
                    Cancel
                </Button>
                <Button variant="contained" color="primary" onClick={handleSave}>
                    Save
                </Button>
            </Box>
            */}

            {/* Pinned bar */}
            <Box
                sx={{
                    position: 'fixed',
                    bottom: 0,
                    left: `${CONFIG.MEASURE.SIDEBAR_WIDTH}px`,
                    right: 0,
                    display: 'flex',
                    //justifyContent: 'flex-end',
                    justifyContent: 'space-between', // Space between the text and buttons
                    alignItems: 'center',
                    //gap: 4,
                    padding: '8px',
                    backgroundColor: isDirty ? '#ffec9e' : '#f9f9f9',
                    boxShadow: '0 -2px 4px rgba(0,0,0,0.1)',
                    borderTop: '1px solid #dcdcdc',
                    //height: '48px',
                    zIndex: 999,
                }}
            >
                {/* Dirty state message or placeholder */}
                <Typography
                    variant="body2"
                    sx={{
                        color: isDirty ? '#6c757d' : 'transparent', // Hide text when not dirty
                        fontWeight: 400,
                        marginLeft: '16px', // Maintain alignment
                    }}
                >
                    Document modified.
                </Typography>

                {/* Buttons */}
                <Box sx={{ display: 'flex', gap: 1, marginRight: '20px' }}>
                    <Button variant="outlined" onClick={handleCancel}>
                        Cancel
                    </Button>
                    <Button
                        variant="contained"
                        color="primary"
                        onClick={handleSave}
                        disabled={!isDirty} // Disable save button if no changes.
                    >
                        Save
                    </Button>
                </Box>
            </Box>
        </Box>
    );
};

export default PersonnelDetails;